import React, {
  useCallback,
  useRef,
  useMemo,
  memo,
  useEffect,
  useState,
} from "react";
import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import { showToast } from "../lib/toast";
import { getErrorMessage } from "../lib/apiErrors";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Heading from "@tiptap/extension-heading";
import CodeMark from "@tiptap/extension-code";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { DOMParser as PMDOMParser } from "@tiptap/pm/model";
import Highlight from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Image from "@tiptap/extension-image";
import { Color } from "@tiptap/extension-color";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ZoomableImage } from "./ZoomableImage";
import { TooltipProvider } from "./ui/tooltip";
import {
  Bold,
  Italic,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Code,
  Link as LinkIcon,
  Highlighter,
  Undo,
  Redo,
  Minus,
  Underline as UnderlineIcon,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
  ImageIcon,
  Palette,
  Table as TableIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Indent,
  Outdent,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  MoreHorizontal,
} from "lucide-react";

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
  editable?: boolean;
  onImageUpload?: (file: File) => Promise<string>;
}

// Derives a stable anchor id from heading text so TOC links like <a href="#foo"> have a target.
// Auto-assigned once per heading — preserved across text edits so renames don't orphan existing links.
const slugifyHeading = (text: string): string =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

// Strips Word/Google Docs-specific HTML markup so pasted content doesn't bloat articles with
// mso-* styles, Office conditional comments, <o:p> XML tags, Docs internal GUIDs, etc.
// Detection: see `hasOfficeCrud` below — only runs cleanup when those markers are present,
// so normal HTML paste is untouched.
const hasOfficeCrud = (html: string): boolean =>
  /\bmso-|<!--\[if|<o:p|<w:|<v:|class="?Mso|docs-internal-guid|<meta[^>]*mso/i.test(html);

// Detects `<pre>` blocks containing `<br>` tags. When users copy from a browser-rendered <pre>,
// Chrome/Firefox often convert visual line breaks to `<br>` tags in the clipboard's text/html payload.
// ProseMirror's CodeBlock parser drops those <br> elements silently because CodeBlock's content is
// `text*` (text-only, not inlineContent — see prosemirror-model DOMParser.leafFallback which only
// converts <br> to \n when the top context is inlineContent). Result: formatted JSON / code gets
// collapsed onto one big line. Fix: replace <br> with literal \n inside <pre> BEFORE ProseMirror parses.
const hasPreWithBr = (html: string): boolean =>
  /<pre[^>]*>[\s\S]*?<br\b[\s\S]*?<\/pre>/i.test(html);

const needsPasteCleanup = (html: string): boolean =>
  hasOfficeCrud(html) || hasPreWithBr(html);

const cleanPastedHtml = (html: string): string => {
  let cleaned = html;
  // Normalize `<br>` → literal `\n` inside `<pre>` blocks BEFORE any other cleanup, so the rest of
  // the pipeline (and ProseMirror's DOMParser downstream) sees real newlines in code block content.
  // See hasPreWithBr for the full explanation — browsers emit <br> in clipboard HTML, CodeBlock's
  // text-only content drops <br>, newlines die. This regex is the inverse of that.
  cleaned = cleaned.replace(
    /(<pre[^>]*>)([\s\S]*?)(<\/pre>)/gi,
    (_m, open: string, content: string, close: string) =>
      open + content.replace(/<br\s*\/?>/gi, "\n") + close,
  );
  // Strip doctype, xml decl, meta, link, style, script (Office/Docs dump a pile of these)
  cleaned = cleaned.replace(/<\?xml[^>]*\?>/gi, "");
  cleaned = cleaned.replace(/<!DOCTYPE[^>]*>/gi, "");
  cleaned = cleaned.replace(/<meta[^>]*>/gi, "");
  cleaned = cleaned.replace(/<link[^>]*>/gi, "");
  cleaned = cleaned.replace(/<style[\s\S]*?<\/style>/gi, "");
  cleaned = cleaned.replace(/<script[\s\S]*?<\/script>/gi, "");
  // Office conditional comments + any other HTML comments (they're never meaningful in paste)
  cleaned = cleaned.replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, "");
  cleaned = cleaned.replace(/<!--[\s\S]*?-->/g, "");
  // Office XML namespace tags: <o:p>, <w:*>, <v:*>, <m:*>
  cleaned = cleaned.replace(/<\/?[owvm]:[^>]*>/gi, "");
  // class="Mso..." / class='Mso...' — drop the whole attribute, safe since TipTap won't render classes anyway.
  // \bMso[A-Z] requires a word boundary + uppercase letter after Mso, matching Office's actual class naming
  // convention (MsoNormal, MsoBodyText, MsoListParagraph, ...) without false-positiving on unrelated class
  // names that merely contain "Mso" as a substring (e.g. `class="Sample Mso Test"`).
  cleaned = cleaned.replace(/\s*class\s*=\s*"[^"]*\bMso[A-Z][^"]*"/g, "");
  cleaned = cleaned.replace(/\s*class\s*=\s*'[^']*\bMso[A-Z][^']*'/g, "");
  // Docs internal guid attributes
  cleaned = cleaned.replace(/\s*id\s*=\s*"docs-internal-guid[^"]*"/gi, "");
  // Filter mso-* entries out of inline style attributes while keeping the rest of the style string
  cleaned = cleaned.replace(/\s*style\s*=\s*"([^"]*)"/gi, (_match, styles: string) => {
    const kept = styles
      .split(";")
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && !/^mso-/i.test(s))
      .join("; ");
    return kept ? ` style="${kept}"` : "";
  });
  return cleaned;
};

const RichTextEditorComponent: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  placeholder = "Start writing your article...",
  className = "",
  editable = true,
  onImageUpload,
}) => {
  const debouncedOnChangeRef = useRef<NodeJS.Timeout | null>(null);
  const onChangeRef = useRef(onChange);
  const onImageUploadRef = useRef(onImageUpload);

  // Keep callback references up to date
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onImageUploadRef.current = onImageUpload;
  }, [onImageUpload]);

  // Stable debounced onChange to prevent editor recreation. The pending content is kept so it can be flushed
  // early: leaving the editor (e.g. pressing Save, which takes the focus) hands it over at once, so the last
  // keystrokes aren't lost to a save that reads the parent's state before the timer fires.
  const pendingContentRef = useRef<string | null>(null);
  const debouncedOnChange = useCallback(
    (newContent: string) => {
      if (debouncedOnChangeRef.current) {
        clearTimeout(debouncedOnChangeRef.current);
      }
      pendingContentRef.current = newContent;
      debouncedOnChangeRef.current = setTimeout(() => {
        debouncedOnChangeRef.current = null;
        pendingContentRef.current = null;
        onChangeRef.current(newContent);
      }, 300);
    },
    [] // No dependencies to keep this stable
  );
  const flushPendingChange = useCallback(() => {
    if (debouncedOnChangeRef.current) {
      clearTimeout(debouncedOnChangeRef.current);
      debouncedOnChangeRef.current = null;
    }
    if (pendingContentRef.current !== null) {
      const pending = pendingContentRef.current;
      pendingContentRef.current = null;
      onChangeRef.current(pending);
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debouncedOnChangeRef.current) {
        clearTimeout(debouncedOnChangeRef.current);
      }
    };
  }, []);

  // Performance-optimized extensions with essential features
  //
  // IMPORTANT: TipTap v3 StarterKit includes by default:
  // NODES: Blockquote, BulletList, CodeBlock, Document, HardBreak, Heading, HorizontalRule, ListItem, OrderedList, Paragraph, Text
  // MARKS: Bold, Code, Italic, Link (v3), Strike, Underline (v3)
  // FUNCTIONALITY: Dropcursor, Gapcursor, Undo/Redo, ListKeymap (v3), TrailingNode (v3)
  //
  // We DISABLE extensions from StarterKit that we want to customize, then add our own
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
        // Disable built-in extensions we want to customize
        link: false, // Disable StarterKit's link to use our custom one
        heading: false, // Disable StarterKit's heading to use our custom one (preserves id attribute for TOC anchor targets)
        code: false, // Disable StarterKit's code mark to use our custom one (allows code to coexist with link + other marks)
      }),
      // Custom Code mark that doesn't exclude other marks. The default Code mark in @tiptap/extension-code
      // sets excludes: "_" which strips all other marks on overlap — so pasting <a><code>text</code></a>
      // drops the Link mark from the code text and splits the anchor, breaking TOC entries that reference
      // inline code (e.g. "the <code>next_page_token</code> cursor"). Overriding excludes to "" lets Code
      // coexist with Link/Bold/Italic/etc., preserving the original HTML structure through parse+serialize.
      CodeMark.extend({
        excludes: "",
      }),
      // Custom Heading that preserves the `id` attribute so TOC links like <a href="#foo"> can jump to <h2 id="foo">
      // and auto-assigns slugified ids to new headings (stable once set, so renames don't orphan TOC links).
      Heading.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            id: {
              default: null,
              parseHTML: (element) => element.getAttribute("id"),
              renderHTML: (attributes) =>
                attributes.id ? { id: attributes.id as string } : {},
            },
          };
        },
        addProseMirrorPlugins() {
          return [
            new Plugin({
              key: new PluginKey("headingAutoId"),
              appendTransaction: (transactions, _oldState, newState) => {
                if (!transactions.some((tr) => tr.docChanged)) return null;

                // First pass: collect unique existing ids; flag duplicate occurrences by position
                // (first occurrence wins, later duplicates get renumbered to preserve HTML uniqueness)
                const usedIds = new Set<string>();
                const duplicatePositions = new Set<number>();
                newState.doc.descendants((node, pos) => {
                  if (node.type.name !== "heading") return true;
                  const existingId = node.attrs.id as string | null;
                  if (existingId) {
                    if (usedIds.has(existingId)) {
                      duplicatePositions.add(pos);
                    } else {
                      usedIds.add(existingId);
                    }
                  }
                  return false;
                });

                // Second pass: assign ids to headings without one OR with duplicate ids
                const tr = newState.tr;
                let modified = false;
                newState.doc.descendants((node, pos) => {
                  if (node.type.name !== "heading") return true;
                  const existingId = node.attrs.id as string | null;
                  const isDuplicate = duplicatePositions.has(pos);
                  if (existingId && !isDuplicate) return false;

                  const baseSlug = slugifyHeading(node.textContent);
                  if (!baseSlug) return false;

                  let finalSlug = baseSlug;
                  let suffix = 2;
                  while (usedIds.has(finalSlug)) {
                    finalSlug = `${baseSlug}-${suffix}`;
                    suffix++;
                  }
                  usedIds.add(finalSlug);

                  tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    id: finalSlug,
                  });
                  modified = true;
                  return false;
                });

                return modified ? tr : null;
              },
            }),
          ];
        },
      }).configure({ levels: [1, 2, 3, 4, 5, 6] }),
      // Our custom Link configuration (replaces StarterKit's link)
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline cursor-pointer",
        },
        validate: (href) => /^(https?:\/\/|#|mailto:|tel:)/.test(href),
      }),

      // Additional functionality extensions (not in StarterKit)
      Placeholder.configure({
        placeholder,
        emptyEditorClass: "is-editor-empty",
      }),
      CharacterCount,

      // Styling extensions (not in StarterKit)
      Highlight.configure({ multicolor: false }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      TextStyle, // Required for advanced text styling

      // Text formatting extensions (not in StarterKit)
      Subscript,
      Superscript,
      Color, // Text color support

      // Content extensions (not in StarterKit)
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: "max-w-full h-auto rounded",
        },
      }),

      // Table extensions (not in StarterKit)
      Table.configure({
        resizable: false, // Disable resizing for performance
        HTMLAttributes: {
          class: "border-collapse border border-border",
        },
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: {
          class: "bg-muted font-bold border border-border p-2",
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: "border border-border p-2",
        },
      }),
    ],
    [placeholder]
  );

  const editor = useEditor(
    {
      extensions: extensions,
      content: content,
      editable,
      immediatelyRender: true,
      shouldRerenderOnTransaction: false,
      onUpdate: ({ editor }) => {
        debouncedOnChange(editor.getHTML());
      },
      onBlur: () => {
        flushPendingChange();
      },
      editorProps: {
        attributes: {
          class: editable
            ? "prose prose-sm sm:prose-base lg:prose-lg xl:prose-xl max-w-none dark:prose-invert focus:outline-none min-h-[200px] p-4"
            : "focus:outline-none",
          spellcheck: "true",
        },
        handlePaste: (view, event) => {
          // Branch 1: image items from clipboard → upload and insert as <img>
          const items = event.clipboardData?.items;
          if (items && onImageUploadRef.current) {
            for (const item of items) {
              if (item.type.startsWith("image/")) {
                event.preventDefault();
                const file = item.getAsFile();
                if (file) {
                  onImageUploadRef.current(file).then((url) => {
                    view.dispatch(
                      view.state.tr.replaceSelectionWith(
                        view.state.schema.nodes.image.create({ src: url })
                      )
                    );
                  }).catch((err) => showToast.error("Image Upload Failed", getErrorMessage(err, "The image could not be uploaded.")));
                }
                return true;
              }
            }
          }

          // Branch 2: HTML that needs preprocessing — either Word/Google Docs crud (mso-* / conditional
          // comments / o:p tags) OR a <pre> block with <br> tags inside (Chrome/Firefox convert visual
          // newlines to <br> when copying from rendered <pre>, and ProseMirror's CodeBlock parser drops
          // <br> silently, collapsing formatted code/JSON onto one line). cleanPastedHtml handles both.
          const clipboardHtml = event.clipboardData?.getData("text/html");
          if (clipboardHtml && needsPasteCleanup(clipboardHtml)) {
            event.preventDefault();
            const cleaned = cleanPastedHtml(clipboardHtml);
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = cleaned;
            const parser = PMDOMParser.fromSchema(view.state.schema);
            const slice = parser.parseSlice(tempDiv, { preserveWhitespace: false });
            view.dispatch(view.state.tr.replaceSelection(slice));
            return true;
          }

          return false;
        },
        handleDrop: (view, event) => {
          const files = event.dataTransfer?.files;
          if (!files?.length || !onImageUploadRef.current) return false;

          const imageFile = Array.from(files).find((f) => f.type.startsWith("image/"));
          if (!imageFile) return false;

          event.preventDefault();
          const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
          onImageUploadRef.current(imageFile).then((url) => {
            const node = view.state.schema.nodes.image.create({ src: url });
            const tr = view.state.tr.insert(pos?.pos ?? view.state.selection.from, node);
            view.dispatch(tr);
          }).catch((err) => showToast.error("Image Upload Failed", getErrorMessage(err, "The image could not be uploaded.")));
          return true;
        },
      },
    },
    [extensions, editable]
  ); // Removed content and debouncedOnChange to prevent recreation

  // Handle external content updates without recreating the editor
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      const { from, to } = editor.state.selection;
      const currentContent = editor.getHTML();

      // Only update if content is actually different
      if (currentContent !== content) {
        // Loading content isn't an edit: without emitUpdate: false the editor's normalized copy of it would come
        // back through onChange and mark an untouched article as changed
        editor.commands.setContent(content, { emitUpdate: false });

        // Try to restore cursor position if the content update was minor
        // This helps maintain editing context during form updates
        if (from === to && editor.state.doc.content.size >= from) {
          editor.commands.setTextSelection(from);
        }
      }
    }
  }, [editor, content]);

  // Attach click handlers in read-only mode: image lightbox + hash anchor smooth scroll + external link navigation.
  // External navigation has to be done explicitly: TipTap's ProseMirror view sits on top of the content and swallows
  // click events for its own handling even when the editor is read-only and openOnClick is false, so the browser's
  // default <a> navigation never fires. We preventDefault + window.open to force the open-in-new-tab behavior readers expect.
  useEffect(() => {
    if (editable || !readOnlyRef.current) return;

    const container = readOnlyRef.current;
    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "IMG" && (target as HTMLImageElement).getAttribute("src")) {
        const images = Array.from(container.querySelectorAll("img"))
          .filter((img) => img.getAttribute("src"));
        const srcs = images.map((img) => img.src);
        const index = images.indexOf(target as HTMLImageElement);
        setLightboxImages(srcs);
        setLightboxIndex(index >= 0 ? index : 0);
        return;
      }

      const anchor = target.closest("a");
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;

        // Hash anchor: scroll within the article container
        if (href.startsWith("#") && href.length > 1) {
          // Always preventDefault — a missing target shouldn't dirty the URL bar or trigger TanStack Router navigation
          e.preventDefault();
          const id = href.slice(1);
          const targetEl = container.querySelector(`[id="${CSS.escape(id)}"]`);
          if (targetEl) {
            targetEl.scrollIntoView({ behavior: "smooth", block: "start" });
          }
          return;
        }

        // External http(s) link: open in a new tab (the browser default is blocked by ProseMirror's view handling)
        if (/^https?:\/\//i.test(href)) {
          const mouseEvent = e as MouseEvent;
          // Let middle-click and modifier-clicks pass through to the browser so users can still force same-tab / background-tab behaviors
          if (mouseEvent.button !== 0 || mouseEvent.metaKey || mouseEvent.ctrlKey || mouseEvent.shiftKey || mouseEvent.altKey) {
            return;
          }
          e.preventDefault();
          window.open(href, "_blank", "noopener,noreferrer");
          return;
        }

        // mailto: / tel: / other schemes: same story — ProseMirror swallows the click, so navigate explicitly.
        if (/^(mailto:|tel:)/i.test(href)) {
          e.preventDefault();
          window.location.href = href;
          return;
        }
      }
    };

    container.addEventListener("click", handleClick);
    return () => container.removeEventListener("click", handleClick);
  }, [editable, editor]);

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrlInput, setLinkUrlInput] = useState("");
  const [articleHeadings, setArticleHeadings] = useState<
    Array<{ id: string; text: string; level: number }>
  >([]);
  const [imageAltDialogOpen, setImageAltDialogOpen] = useState(false);
  const [imageAltInput, setImageAltInput] = useState("");
  const [pendingImageSrc, setPendingImageSrc] = useState<string | null>(null);
  // True when the dialog was opened by clicking an already-inserted image (edit alt text)
  // rather than by uploading a new one. Determines whether submit calls setImage or updateAttributes.
  const [isEditingExistingImage, setIsEditingExistingImage] = useState(false);

  const openLinkDialog = useCallback(() => {
    if (!editor) return;
    const previousUrl = (editor.getAttributes("link").href as string | undefined) ?? "";
    setLinkUrlInput(previousUrl);

    // Snapshot current heading list so the dialog can offer them as jump targets
    const headings: Array<{ id: string; text: string; level: number }> = [];
    editor.state.doc.descendants((node) => {
      if (
        node.type.name === "heading" &&
        node.attrs.id &&
        node.textContent.trim()
      ) {
        headings.push({
          id: node.attrs.id as string,
          text: node.textContent,
          level: node.attrs.level as number,
        });
      }
      return true;
    });
    setArticleHeadings(headings);
    setLinkDialogOpen(true);
  }, [editor]);

  const submitLinkDialog = useCallback(() => {
    if (!editor) return;
    const url = linkUrlInput.trim();
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      // Ensure URL has protocol — but let hash anchors, mailto:, and tel: through untouched
      const fullUrl = /^(https?:\/\/|#|mailto:|tel:)/.test(url) ? url : `https://${url}`;
      editor.chain().focus().extendMarkRange("link").setLink({ href: fullUrl }).run();
    }
    setLinkDialogOpen(false);
  }, [editor, linkUrlInput]);

  const addImage = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      // Resolve the image src (either via blob upload or base64 fallback), then open the alt-text dialog
      // so the author can describe it. The image is inserted only after the dialog is submitted.
      let src: string | null = null;
      if (onImageUpload) {
        try {
          src = await onImageUpload(file);
        } catch (err) {
          showToast.error('Image Upload Failed', getErrorMessage(err, 'The image could not be uploaded.'));
          return;
        }
      } else {
        src = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve((e.target?.result as string) || "");
          reader.readAsDataURL(file);
        });
      }
      if (!src) return;
      setPendingImageSrc(src);
      setImageAltInput("");
      setImageAltDialogOpen(true);
    };
    input.click();
  }, [onImageUpload]);

  const submitImageAltDialog = useCallback(() => {
    if (!editor) {
      setImageAltDialogOpen(false);
      setPendingImageSrc(null);
      setIsEditingExistingImage(false);
      return;
    }
    const altValue = imageAltInput.trim();
    if (isEditingExistingImage) {
      // Updating alt on the currently-selected image. updateAttributes operates on the active
      // node selection — clicking an <img> in edit mode sets a NodeSelection on the image,
      // so this applies to the right node.
      editor.chain().focus().updateAttributes("image", { alt: altValue }).run();
    } else if (pendingImageSrc) {
      // Inserting a new image (toolbar upload path). Empty alt is valid for decorative images —
      // emit alt="" explicitly so screen readers skip it instead of announcing the filename.
      editor
        .chain()
        .focus()
        .setImage({ src: pendingImageSrc, alt: altValue })
        .run();
    }
    setImageAltDialogOpen(false);
    setPendingImageSrc(null);
    setImageAltInput("");
    setIsEditingExistingImage(false);
  }, [editor, pendingImageSrc, imageAltInput, isEditingExistingImage]);

  const cancelImageAltDialog = useCallback(() => {
    // Cancel = don't insert (new image path) or don't change alt (existing image path).
    // For new images, the uploaded blob will be orphan-cleaned by DataRetentionService after 30d.
    setImageAltDialogOpen(false);
    setPendingImageSrc(null);
    setImageAltInput("");
    setIsEditingExistingImage(false);
    editor?.commands.focus();
  }, [editor]);

  // Click an existing image in edit mode to reopen the alt-text dialog pre-populated.
  // Required because paste/drag-and-drop insert images with empty alt (they skip the toolbar
  // dialog for speed), and otherwise there'd be no way for the author to add descriptive alt later.
  useEffect(() => {
    if (!editable || !editor) return;
    const editorDom = editor.view.dom;
    const handleImageClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName !== "IMG") return;
      const img = target as HTMLImageElement;
      setPendingImageSrc(img.getAttribute("src") || null);
      setImageAltInput(img.getAttribute("alt") || "");
      setIsEditingExistingImage(true);
      setImageAltDialogOpen(true);
    };
    editorDom.addEventListener("click", handleImageClick);
    return () => editorDom.removeEventListener("click", handleImageClick);
  }, [editable, editor]);

  // Cmd/Ctrl+K to open the link dialog. Bound at the DOM level on the editor's root because our
  // custom Link config doesn't expose addKeyboardShortcuts (disabling StarterKit's Link removed the
  // default binding, and @tiptap/extension-link 3.x doesn't register Mod-k itself either).
  useEffect(() => {
    if (!editable || !editor) return;
    const editorDom = editor.view.dom;
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if the dialog is already open — re-running openLinkDialog() would churn state setters
      // (setArticleHeadings always creates a fresh array) for no visible effect.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k" && !linkDialogOpen) {
        e.preventDefault();
        openLinkDialog();
      }
    };
    editorDom.addEventListener("keydown", handleKeyDown);
    return () => editorDom.removeEventListener("keydown", handleKeyDown);
  }, [editable, editor, openLinkDialog, linkDialogOpen]);

  const [showColorPicker, setShowColorPicker] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const readOnlyRef = useRef<HTMLDivElement>(null);

  const commonColors = [
    "#000000",
    "#434343",
    "#666666",
    "#999999",
    "#b7b7b7",
    "#cccccc",
    "#d9d9d9",
    "#efefef",
    "#f3f3f3",
    "#ffffff",
    "#980000",
    "#ff0000",
    "#ff9900",
    "#ffff00",
    "#00ff00",
    "#00ffff",
    "#4a86e8",
    "#0000ff",
    "#9900ff",
    "#ff00ff",
    "#e6b8af",
    "#f4cccc",
    "#fce5cd",
    "#fff2cc",
    "#d9ead3",
    "#d0e0e3",
    "#c9daf8",
    "#cfe2f3",
    "#d9d2e9",
    "#ead1dc",
    "#dd7e6b",
    "#ea9999",
    "#f9cb9c",
    "#ffe599",
    "#b6d7a8",
    "#a2c4c9",
    "#a4c2f4",
    "#9fc5e8",
    "#b4a7d6",
    "#d5a6bd",
    "#cc4125",
    "#e06666",
    "#f6b26b",
    "#ffd966",
    "#93c47d",
    "#76a5af",
    "#6d9eeb",
    "#6fa8dc",
    "#8e7cc3",
    "#c27ba0",
    "#a61c00",
    "#cc0000",
    "#e69138",
    "#f1c232",
    "#6aa84f",
    "#45818e",
    "#3c78d8",
    "#3d85c6",
    "#674ea7",
    "#a64d79",
    "#85200c",
    "#990000",
    "#b45f06",
    "#bf9000",
    "#38761d",
    "#134f5c",
    "#1155cc",
    "#0b5394",
    "#351c75",
    "#741b47",
    "#5b0f00",
    "#660000",
    "#783f04",
    "#7f6000",
    "#274e13",
    "#0c343d",
    "#1c4587",
    "#073763",
    "#20124d",
    "#4c1130",
  ];

  const setTextColor = useCallback(
    (color: string) => {
      if (color === "") {
        editor?.chain().focus().unsetColor().run();
      } else {
        editor?.chain().focus().setColor(color).run();
      }
      setShowColorPicker(false);
    },
    [editor]
  );

  const resetTextColor = useCallback(() => {
    editor?.chain().focus().unsetColor().run();
    setShowColorPicker(false);
  }, [editor]);

  const insertTable = useCallback(() => {
    editor
      ?.chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
  }, [editor]);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target as Node)
      ) {
        setShowColorPicker(false);
      }
    };

    if (showColorPicker) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showColorPicker]);

  // Minimal reactive state for essential UI updates only
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      if (!ctx.editor) {
        return {
          characters: 0,
          words: 0,
        };
      }

      return {
        // Character count - only essential info
        characters: ctx.editor.storage.characterCount?.characters() || 0,
        words: ctx.editor.storage.characterCount?.words() || 0,
      };
    },
  });

  // Optimized toolbar state using useEditorState for performance
  const toolbarState = useEditorState({
    editor,
    selector: (ctx) => {
      if (!ctx.editor) {
        return {
          bold: false,
          italic: false,
          underline: false,
          strike: false,
          highlight: false,
          subscript: false,
          superscript: false,
          headingLevel: 0 as 0 | 1 | 2 | 3 | 4 | 5 | 6,
          bulletList: false,
          orderedList: false,
          blockquote: false,
          code: false,
          link: false,
          canUndo: false,
          canRedo: false,
          hasColor: false,
          inTable: false,
          alignLeft: false,
          alignCenter: false,
          alignRight: false,
          alignJustify: false,
        };
      }

      return {
        bold: ctx.editor.isActive("bold"),
        italic: ctx.editor.isActive("italic"),
        underline: ctx.editor.isActive("underline"),
        strike: ctx.editor.isActive("strike"),
        highlight: ctx.editor.isActive("highlight"),
        subscript: ctx.editor.isActive("subscript"),
        superscript: ctx.editor.isActive("superscript"),
        headingLevel: ([1, 2, 3, 4, 5, 6] as const).find((lvl) =>
          ctx.editor!.isActive("heading", { level: lvl })
        ) ?? 0,
        bulletList: ctx.editor.isActive("bulletList"),
        orderedList: ctx.editor.isActive("orderedList"),
        blockquote: ctx.editor.isActive("blockquote"),
        code: ctx.editor.isActive("code"),
        link: ctx.editor.isActive("link"),
        canUndo: ctx.editor.can().undo(),
        canRedo: ctx.editor.can().redo(),
        hasColor: ctx.editor.isActive("textStyle", { color: /.+/ }),
        inTable: ctx.editor.isActive("table"),
        alignLeft: ctx.editor.isActive({ textAlign: "left" }),
        alignCenter: ctx.editor.isActive({ textAlign: "center" }),
        alignRight: ctx.editor.isActive({ textAlign: "right" }),
        alignJustify: ctx.editor.isActive({ textAlign: "justify" }),
      };
    },
  });

  if (!editor) {
    return null;
  }

  if (!editable) {
    const lightboxOpen = lightboxIndex >= 0 && lightboxImages.length > 0;
    const currentSrc = lightboxOpen ? lightboxImages[lightboxIndex] : null;
    const hasPrev = lightboxIndex > 0;
    const hasNext = lightboxIndex < lightboxImages.length - 1;

    return (
      <>
        <div className={className} ref={readOnlyRef} role="article">
          <EditorContent editor={editor} />
        </div>
        <Dialog open={lightboxOpen} onOpenChange={(open) => { if (!open) setLightboxIndex(-1); }}>
          <DialogContent className="max-w-[90vw] max-h-[90vh] p-4 flex flex-col items-center bg-background/95 border-none gap-2">
            {currentSrc && (
              <>
                <div className="flex-1 min-h-0 w-full">
                  <ZoomableImage src={currentSrc} />
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {lightboxImages.length > 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setLightboxIndex((i) => i - 1)}
                      disabled={!hasPrev}
                      aria-label="Previous image" title="Previous image"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                  )}
                  {lightboxImages.length > 1 && (
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {lightboxIndex + 1} / {lightboxImages.length}
                    </span>
                  )}
                  {lightboxImages.length > 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setLightboxIndex((i) => i + 1)}
                      disabled={!hasNext}
                      aria-label="Next image" title="Next image"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  )}
                  <a href={currentSrc} download target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" aria-label="Download image" title="Download image">
                      <Download className="h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
    <TooltipProvider>
      <div className={`border border-input rounded-lg ${className}`}>
        {/* Performance-Optimized Toolbar with Essential Features */}
        <div className="flex flex-wrap gap-0.5 min-[360px]:gap-1 p-1 min-[360px]:p-2 border-b border-input bg-muted/50">
          {/* Basic text formatting - Always visible */}
          <div className="flex gap-0.5 min-[360px]:gap-1 border-r border-input pr-1 min-[360px]:pr-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={toolbarState.bold ? "bg-muted" : ""}
              aria-label="Bold" title="Bold"
            >
              <Bold className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={toolbarState.italic ? "bg-muted" : ""}
              aria-label="Italic" title="Italic"
            >
              <Italic className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={toolbarState.underline ? "bg-muted" : ""}
              aria-label="Underline" title="Underline"
            >
              <UnderlineIcon className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleStrike().run()}
              className={toolbarState.strike ? "bg-muted" : ""}
              aria-label="Strikethrough" title="Strikethrough"
            >
              <Strikethrough className="h-4 w-4" />
            </Button>

          </div>

          {/* Extended formatting - Hidden on 320px, visible 360px+ */}
          <div className="hidden min-[360px]:flex gap-0.5 min-[360px]:gap-1 border-r border-input pr-1 min-[360px]:pr-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleHighlight().run()}
              className={toolbarState.highlight ? "bg-muted" : ""}
              aria-label="Highlight" title="Highlight"
            >
              <Highlighter className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleSubscript().run()}
              className={toolbarState.subscript ? "bg-muted" : ""}
              aria-label="Subscript" title="Subscript"
            >
              <SubscriptIcon className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleSuperscript().run()}
              className={toolbarState.superscript ? "bg-muted" : ""}
              aria-label="Superscript" title="Superscript"
            >
              <SuperscriptIcon className="h-4 w-4" />
            </Button>
          </div>

          {/* Headings - Hidden on 320px, visible 360px+. Single dropdown covering Paragraph + H1-H6 */}
          <div className="hidden min-[360px]:flex gap-0.5 min-[360px]:gap-1 border-r border-input pr-1 min-[360px]:pr-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={toolbarState.headingLevel > 0 ? "bg-muted min-w-[3rem]" : "min-w-[3rem]"}
                  aria-label={toolbarState.headingLevel > 0 ? `Heading ${toolbarState.headingLevel}` : "Paragraph"}
                  title="Heading level"
                >
                  <span className="text-sm font-semibold">
                    {toolbarState.headingLevel > 0 ? `H${toolbarState.headingLevel}` : "P"}
                  </span>
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[8rem]">
                <DropdownMenuItem
                  onClick={() => editor.chain().focus().setParagraph().run()}
                  className={toolbarState.headingLevel === 0 ? "bg-muted" : ""}
                >
                  <span className="font-mono text-xs text-muted-foreground w-8 inline-block">P</span>
                  <span>Paragraph</span>
                </DropdownMenuItem>
                {([1, 2, 3, 4, 5, 6] as const).map((level) => (
                  <DropdownMenuItem
                    key={level}
                    onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
                    className={toolbarState.headingLevel === level ? "bg-muted" : ""}
                  >
                    <span className="font-mono text-xs text-muted-foreground w-8 inline-block">H{level}</span>
                    <span>Heading {level}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Lists and formatting - Hidden on 320px, visible 480px+ */}
          <div className="hidden min-[480px]:flex gap-0.5 min-[360px]:gap-1 border-r border-input pr-1 min-[360px]:pr-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={toolbarState.bulletList ? "bg-muted" : ""}
              aria-label="Bullet List" title="Bullet List"
            >
              <List className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={toolbarState.orderedList ? "bg-muted" : ""}
              aria-label="Numbered List" title="Numbered List"
            >
              <ListOrdered className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className={toolbarState.blockquote ? "bg-muted" : ""}
              aria-label="Quote" title="Quote"
            >
              <Quote className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleCode().run()}
              className={toolbarState.code ? "bg-muted" : ""}
              aria-label="Code" title="Code"
            >
              <Code className="h-4 w-4" />
            </Button>
          </div>

          {/* Links and media - Hidden on 320px, visible 480px+ */}
          <div className="hidden min-[480px]:flex gap-0.5 min-[360px]:gap-1 border-r border-input pr-1 min-[360px]:pr-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={openLinkDialog}
              className={toolbarState.link ? "bg-muted" : ""}
              aria-label="Add Link" title="Add Link"
            >
              <LinkIcon className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
              aria-label="Horizontal Line" title="Horizontal Line"
            >
              <Minus className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addImage}
              aria-label="Add Image" title="Add Image"
            >
              <ImageIcon className="h-4 w-4" />
            </Button>

            <div className="relative">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowColorPicker(!showColorPicker)}
                className={toolbarState.hasColor ? "bg-muted" : ""}
                aria-label="Text Color" title="Text Color"
              >
                <Palette className="h-4 w-4" />
                <ChevronDown className="h-3 w-3 ml-1" />
              </Button>

              {showColorPicker && (
                <div
                  ref={colorPickerRef}
                  className="absolute top-full left-0 mt-1 p-3 bg-background border border-input rounded-lg shadow-lg z-50 w-[280px] min-[360px]:w-64 max-w-[calc(100vw-2rem)]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Text Color</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={resetTextColor}
                      className="text-xs"
                    >
                      Reset
                    </Button>
                  </div>
                  <div className="grid grid-cols-10 gap-1">
                    {commonColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                        style={{ backgroundColor: color }}
                        onClick={() => setTextColor(color)}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={insertTable}
              className={toolbarState.inTable ? "bg-muted" : ""}
              aria-label="Insert Table" title="Insert Table"
            >
              <TableIcon className="h-4 w-4" />
            </Button>
          </div>

          {/* Text Alignment - Hidden on 320px, visible 480px+ */}
          <div className="hidden min-[480px]:flex gap-0.5 min-[360px]:gap-1 border-r border-input pr-1 min-[360px]:pr-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().setTextAlign("left").run()}
              className={toolbarState.alignLeft ? "bg-muted" : ""}
              aria-label="Align Left" title="Align Left"
            >
              <AlignLeft className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                editor.chain().focus().setTextAlign("center").run()
              }
              className={toolbarState.alignCenter ? "bg-muted" : ""}
              aria-label="Align Center" title="Align Center"
            >
              <AlignCenter className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().setTextAlign("right").run()}
              className={toolbarState.alignRight ? "bg-muted" : ""}
              aria-label="Align Right" title="Align Right"
            >
              <AlignRight className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                editor.chain().focus().setTextAlign("justify").run()
              }
              className={toolbarState.alignJustify ? "bg-muted" : ""}
              aria-label="Justify Text" title="Justify Text"
            >
              <AlignJustify className="h-4 w-4" />
            </Button>
          </div>

          {/* Indentation - Hidden on 320px, visible 480px+ */}
          <div className="hidden min-[480px]:flex gap-0.5 min-[360px]:gap-1 border-r border-input pr-1 min-[360px]:pr-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                editor.chain().focus().liftListItem("listItem").run()
              }
              aria-label="Decrease Indent" title="Decrease Indent"
            >
              <Outdent className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                editor.chain().focus().sinkListItem("listItem").run()
              }
              aria-label="Increase Indent" title="Increase Indent"
            >
              <Indent className="h-4 w-4" />
            </Button>
          </div>

          {/* Mobile overflow menu - visible only below 480px. Exposes the toolbar sections that
              are hidden at narrow widths (Headings, Lists, Link, Image, HR) so authors on phones
              can still structure articles. At ≥480px this is hidden because the full toolbar is available. */}
          <div className="flex min-[480px]:hidden gap-0.5 border-r border-input pr-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label="More formatting options"
                  title="More"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[11rem]">
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
                  <span className="font-mono text-xs text-muted-foreground w-8 inline-block">H1</span>
                  <span>Heading 1</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
                  <span className="font-mono text-xs text-muted-foreground w-8 inline-block">H2</span>
                  <span>Heading 2</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
                  <span className="font-mono text-xs text-muted-foreground w-8 inline-block">H3</span>
                  <span>Heading 3</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleBulletList().run()}>
                  <List className="h-4 w-4 mr-2" />
                  <span>Bullet list</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                  <ListOrdered className="h-4 w-4 mr-2" />
                  <span>Numbered list</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleBlockquote().run()}>
                  <Quote className="h-4 w-4 mr-2" />
                  <span>Quote</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleCode().run()}>
                  <Code className="h-4 w-4 mr-2" />
                  <span>Inline code</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={openLinkDialog}>
                  <LinkIcon className="h-4 w-4 mr-2" />
                  <span>Link</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={addImage}>
                  <ImageIcon className="h-4 w-4 mr-2" />
                  <span>Image</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().setHorizontalRule().run()}>
                  <Minus className="h-4 w-4 mr-2" />
                  <span>Horizontal rule</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Undo/Redo - Always visible */}
          <div className="flex gap-0.5 min-[360px]:gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!toolbarState.canUndo}
              aria-label="Undo" title="Undo"
            >
              <Undo className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!toolbarState.canRedo}
              aria-label="Redo" title="Redo"
            >
              <Redo className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Editor content */}
        <div className="min-h-[200px] max-h-[600px] overflow-y-auto">
          <EditorContent editor={editor} />
        </div>

        {/* Footer info */}
        <div className="flex justify-between items-center p-2 border-t border-input bg-muted/50">
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span>{editorState.characters} characters</span>
            <span>{editorState.words} words</span>
          </div>
        </div>
      </div>
    </TooltipProvider>
    <Dialog
      open={linkDialogOpen}
      onOpenChange={(open) => {
        setLinkDialogOpen(open);
        // Refocus the editor on every close path (X, Escape, Cancel, click-outside) so the author
        // can keep typing without an extra click. Save path also calls focus() explicitly — duplicate is harmless.
        if (!open) editor?.commands.focus();
      }}
    >
      <DialogContent className="max-w-[min(32rem,calc(100vw-2rem))] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Insert link</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2 min-w-0">
          <div className="flex flex-col gap-1.5 min-w-0">
            <Label htmlFor="rte-link-url">URL</Label>
            <Input
              id="rte-link-url"
              value={linkUrlInput}
              onChange={(e) => setLinkUrlInput(e.target.value)}
              placeholder="https://example.com"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitLinkDialog();
                }
              }}
              autoFocus
              className="w-full"
            />
            <p className="text-xs text-muted-foreground break-words">
              Leave empty to remove the link. Use <code className="font-mono">#heading-id</code> for an in-article anchor.
            </p>
          </div>
          {articleHeadings.length > 0 && (
            <div className="flex flex-col gap-1.5 min-w-0">
              <Label id="rte-heading-picker-label">Or link to a heading in this article</Label>
              <div
                role="listbox"
                aria-labelledby="rte-heading-picker-label"
                className="max-h-48 overflow-y-auto overflow-x-hidden rounded-md border border-input focus:outline-none"
              >
                {articleHeadings.map((h, i) => (
                  <button
                    key={`${h.id}-${i}`}
                    type="button"
                    role="option"
                    aria-selected={linkUrlInput === `#${h.id}`}
                    onClick={() => setLinkUrlInput(`#${h.id}`)}
                    onKeyDown={(e) => {
                      // Arrow-key navigation between headings in the picker. Tab still moves to the
                      // next focusable element outside the listbox, matching standard listbox semantics.
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        const next = e.currentTarget.nextElementSibling as HTMLElement | null;
                        next?.focus();
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        const prev = e.currentTarget.previousElementSibling as HTMLElement | null;
                        prev?.focus();
                      } else if (e.key === "Home") {
                        e.preventDefault();
                        (e.currentTarget.parentElement?.firstElementChild as HTMLElement | null)?.focus();
                      } else if (e.key === "End") {
                        e.preventDefault();
                        (e.currentTarget.parentElement?.lastElementChild as HTMLElement | null)?.focus();
                      }
                    }}
                    className="block w-full px-3 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none border-b border-input last:border-b-0"
                  >
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="font-mono text-xs text-muted-foreground w-6 flex-shrink-0">H{h.level}</span>
                      <span className="text-sm truncate flex-1 min-w-0">{h.text}</span>
                    </div>
                    <div className="font-mono text-xs text-muted-foreground truncate pl-8 mt-0.5">#{h.id}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button onClick={submitLinkDialog}>Save link</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <Dialog open={imageAltDialogOpen} onOpenChange={(open) => { if (!open) cancelImageAltDialog(); }}>
      <DialogContent className="max-w-[min(32rem,calc(100vw-2rem))] overflow-hidden">
        <DialogHeader>
          <DialogTitle>{isEditingExistingImage ? "Edit alt text" : "Describe the image"}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2 min-w-0">
          {pendingImageSrc && (
            <div className="flex justify-center rounded-md border border-input bg-muted/30 p-2">
              <img
                src={pendingImageSrc}
                alt="Preview"
                className="max-h-40 max-w-full object-contain"
              />
            </div>
          )}
          <div className="flex flex-col gap-1.5 min-w-0">
            <Label htmlFor="rte-image-alt">Alt text</Label>
            <Input
              id="rte-image-alt"
              value={imageAltInput}
              onChange={(e) => setImageAltInput(e.target.value)}
              placeholder="e.g. Screenshot of the login page"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitImageAltDialog();
                }
              }}
              autoFocus
              className="w-full"
            />
            <p className="text-xs text-muted-foreground break-words">
              Describe what the image shows for screen-reader users. Leave empty only for purely decorative images.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={cancelImageAltDialog}>Cancel</Button>
          <Button onClick={submitImageAltDialog}>
            {isEditingExistingImage ? "Save alt text" : "Insert image"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

// Memoize component to prevent unnecessary re-renders
export const RichTextEditor = memo(RichTextEditorComponent);
